import axios from 'axios';

/**
 * Safely decodes a JWT without full verification (frontend only).
 * @param {string} token
 * @returns {any} Decoded payload or null if invalid
 */
export const decodeJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

const PROVIDER_TOKEN_ENDPOINT =
  process.env.REACT_APP_PROVIDER_TOKEN_ENDPOINT ||
  'https://q-accounts.qdx.global/realms/quidax/rest/internal/provider-token';

// In-memory cache
let legacyTokenCache = null;
// In-flight promise for de-duplication
let inFlightRequest = null;

/**
 * Clears the legacy token cache.
 * Should be called on logout or user switch.
 */
export const clearLegacyTokenCache = () => {
  legacyTokenCache = null;
  inFlightRequest = null;
};

/**
 * Resolves the correct API access token.
 * 1. If legacySource exists in IdP token, fetch/return legacy token.
 * 2. Otherwise return original IdP token.
 *
 * @param {string} idpAccessToken
 * @returns {Promise<string>}
 */
export const resolveApiAccessToken = async (idpAccessToken) => {
  if (!idpAccessToken) {
    throw new Error('IDP_TOKEN_MISSING');
  }

  const decoded = decodeJwt(idpAccessToken);
  if (!decoded) {
    throw new Error('IDP_TOKEN_INVALID');
  }

  const legacySource = decoded.legacySource;

  const supportLegacySourceToken = process.env.REACT_APP_SUPPORT_LEGACY_SOURCE_TOKEN === 'true';

  // If legacy tokens are not supported, return the original IdP token
  if (!supportLegacySourceToken) {
    return idpAccessToken;
  }

  // If no legacySource, use IdP token directly
  if (!legacySource || !legacySource.token_ref) {
    return idpAccessToken;
  }

  const cacheKey = `${decoded.sub}:${legacySource.provider}:${legacySource.subject}:${legacySource.token_ref}`;
  const now = Date.now();
  const EXPIRY_BUFFER_MS = 30 * 1000; // 30 seconds

  // Check cache
  if (
    legacyTokenCache &&
    legacyTokenCache.cacheKey === cacheKey &&
    legacyTokenCache.expiresAt > now + EXPIRY_BUFFER_MS
  ) {
    return legacyTokenCache.token;
  }

  // If already fetching, return the existing promise
  if (inFlightRequest && inFlightRequest.cacheKey === cacheKey) {
    return inFlightRequest.promise;
  }

  // Fetch new legacy token
  const fetchPromise = (async () => {
    try {
      const response = await axios.get(`${PROVIDER_TOKEN_ENDPOINT}/${legacySource.token_ref}`, {
        headers: {
          Authorization: `Bearer ${idpAccessToken}`
        },
        // Avoid recursive interceptor loops if this were to use the main instance
        // but here we use a fresh axios instance.
        _isProviderTokenRequest: true
      });

      const { token, expiresAt } = response.data;

      if (!token) {
        throw new Error('PROVIDER_TOKEN_MISSING_IN_RESPONSE');
      }

      // Update cache
      legacyTokenCache = {
        token,
        expiresAt, // Assuming this is already in ms as per requirement
        cacheKey,
        sourceMetadata: legacySource
      };

      return token;
    } catch (error) {
      if (error.message === 'PROVIDER_TOKEN_MISSING_IN_RESPONSE') {
        throw error;
      }
      console.error('Failed to fetch provider token:', error);
      throw new Error('PROVIDER_TOKEN_FETCH_FAILED');
    } finally {
      inFlightRequest = null;
    }
  })();

  inFlightRequest = {
    cacheKey,
    promise: fetchPromise
  };

  return fetchPromise;
};
